/**
 * FilterDropdownView - Displays filter selection dropdown
 *
 * SE-5570: renders through the shared `SelectDropdown` molecule
 * (FixedPositionDropdownShell) instead of the retired gluestack Menu atom.
 * The shell hosts the panel in a native Modal above all content (the old
 * `useRNModal` fork dies — the shell owns native hosting), so the SE-3823
 * Skia-touch-interception workaround is no longer needed. Open state is
 * controlled by the container.
 * @remarks This is a View component - contains no business logic, hooks, or state
 */

import { ChevronDown } from "lucide-react-native";
import { memo } from "react";

import { Icon } from "@/components/atoms/Icon";
import { Stack } from "@/components/atoms/Stack";
import { Text } from "@/components/atoms/Text";
import SelectDropdown, {
  type SelectDropdownOption,
} from "@/components/molecules/SelectDropdown";

import type { PerformanceFilter } from "@/features/player-detail/types";

/**
 * Filter configuration
 */
interface FilterConfig {
  readonly id: PerformanceFilter;
  readonly label: string;
}

/**
 * Props for FilterDropdownView component
 */
interface FilterDropdownViewProps {
  readonly isDark: boolean;
  readonly activeFilter: PerformanceFilter;
  readonly filterLabel: string;
  readonly onSelectFilter: (filter: PerformanceFilter) => void;
  readonly isOpen: boolean;
  readonly onToggle: () => void;
  readonly onClose: () => void;
}

const FILTERS: readonly FilterConfig[] = [
  { id: "last10", label: "Last 10 games" },
  { id: "last1000min", label: "Last 1000 minutes" },
  { id: "last60days", label: "Last 60 days" },
] as const;

/**
 * Builds the filter options for the SelectDropdown, marking the active filter.
 * @param activeFilter - Currently selected filter ID
 * @param onSelectFilter - Invoked with the chosen filter ID
 * @returns The filter options in SelectDropdown shape
 */
const buildFilterOptions = (
  activeFilter: PerformanceFilter,
  onSelectFilter: (filter: PerformanceFilter) => void
): SelectDropdownOption[] =>
  FILTERS.map(filter => ({
    id: filter.id,
    label: filter.label,
    selected: activeFilter === filter.id,
    onPress: () => onSelectFilter(filter.id),
  }));

/**
 * FilterDropdownView renders the filter selection dropdown via SelectDropdown.
 * @param props - View props
 * @param props.isDark - Whether dark theme is active
 * @param props.activeFilter - Currently selected filter ID
 * @param props.filterLabel - Display label for the active filter
 * @param props.onSelectFilter - Invoked with the chosen filter ID
 * @param props.isOpen - Whether the dropdown is open
 * @param props.onToggle - Toggle the dropdown (trigger press)
 * @param props.onClose - Close the dropdown
 * @returns SelectDropdown with filter options
 */
const FilterDropdownView = ({
  activeFilter,
  filterLabel,
  onSelectFilter,
  isOpen,
  onToggle,
  onClose,
}: FilterDropdownViewProps) => (
  <SelectDropdown
    isOpen={isOpen}
    onToggle={onToggle}
    onClose={onClose}
    anchor="right"
    panelClassName="max-w-56"
    triggerAccessibilityLabel="Select performance filter"
    triggerContent={
      <Stack
        direction="horizontal"
        className="items-center gap-1 rounded-none bg-surface-raised px-2 py-1"
      >
        <Text variant="caption" className="text-content-primary">
          {filterLabel}
        </Text>
        <Icon as={ChevronDown} size="sm" className="text-content-primary" />
      </Stack>
    }
    options={buildFilterOptions(activeFilter, onSelectFilter)}
  />
);

FilterDropdownView.displayName = "FilterDropdownView";

export default memo(FilterDropdownView);
